import { useCallback } from 'react';
import { useModal } from '../contexts/ModalContext';
import { apiService } from '../services/api';
import { logger } from '../utils/logger';
import { User } from '../types';

interface UseWalkthroughSequenceProps {
  user: User | null;
  setUser: (user: User | null) => void;
  navigate: (path: string) => void;
  refreshFounders?: (() => Promise<void>) | null;
  setCustomFieldsRefreshTrigger?: React.Dispatch<React.SetStateAction<number>>;
}

export function useWalkthroughSequence({
  user,
  setUser,
  navigate,
  refreshFounders,
  setCustomFieldsRefreshTrigger,
}: UseWalkthroughSequenceProps) {
  const {
    openModal,
    closeModal,
    walkthroughFromOnboarding,
    onboardingWalkthroughStep,
    setWalkthroughSequence,
  } = useModal();

  // Start the product walkthrough sequence
  const startProductWalkthrough = useCallback(() => {
    openModal('productWalkthrough', {
      onComplete: async () => {
        closeModal();
        try {
          await apiService.completeWalkthrough();
          if (user) {
            setUser({ ...user, walkthroughCompleted: true });
          }
          // After product walkthrough, show phone walkthrough
          setTimeout(() => {
            openModal('phoneWalkthrough');
          }, 500);
        } catch (error) {
          logger.error('Failed to mark walkthrough as completed:', error);
          // Still show phone walkthrough even if API fails
          setTimeout(() => {
            openModal('phoneWalkthrough');
          }, 500);
        }
      },
    });
  }, [openModal, closeModal, user, setUser]);

  // Handle phone walkthrough close
  const handlePhoneWalkthroughClose = useCallback(async () => {
    closeModal();

    if (walkthroughFromOnboarding && onboardingWalkthroughStep === 3) {
      // Complete the onboarding sequence
      try {
        const response = await apiService.completeWalkthrough();
        setUser(response.user);
        setWalkthroughSequence(false, 0);
        navigate('/app/people');
      } catch (err) {
        logger.error('Error completing walkthrough:', err);
        setWalkthroughSequence(false, 0);
        navigate('/app/people');
      }
    } else {
      // Show LinkedIn import if not imported/dismissed
      const isDismissed = localStorage.getItem('linkedInImportDismissed') === 'true';
      if (user && !user.connectionsImported && !isDismissed) {
        setTimeout(() => {
          openModal('linkedInImport');
        }, 500);
      }
    }
  }, [
    walkthroughFromOnboarding,
    onboardingWalkthroughStep,
    user,
    setUser,
    navigate,
    closeModal,
    openModal,
    setWalkthroughSequence,
  ]);

  // Handle chrome extension walkthrough close
  const handleChromeExtensionClose = useCallback(() => {
    closeModal();
    if (walkthroughFromOnboarding && onboardingWalkthroughStep === 2) {
      setWalkthroughSequence(true, 3);
      openModal('phoneWalkthrough');
    }
  }, [
    walkthroughFromOnboarding,
    onboardingWalkthroughStep,
    closeModal,
    openModal,
    setWalkthroughSequence,
  ]);

  // Handle LinkedIn import completion
  const handleLinkedInImportComplete = useCallback(async (skipped?: boolean) => {
    try {
      closeModal();
      if (!skipped) {
        const response = await apiService.getCurrentUser();
        const userData = response.user || response;
        setUser(userData);
        if (refreshFounders) {
          try {
            await refreshFounders();
          } catch (refreshError) {
            console.error('Error refreshing founders after import:', refreshError);
          }
        }
      }
      localStorage.removeItem('onboardingFormData');
    } catch (err) {
      logger.error('Error refreshing user data after LinkedIn import:', err);
      localStorage.removeItem('onboardingFormData');
    }
  }, [setUser, refreshFounders, closeModal]);

  // Handle Notion import completion
  const handleNotionImportComplete = useCallback(async (skipped?: boolean) => {
    try {
      closeModal();
      if (!skipped) {
        const response = await apiService.getCurrentUser();
        const userData = response.user || response;
        setUser(userData);
        if (setCustomFieldsRefreshTrigger) {
          setCustomFieldsRefreshTrigger(prev => prev + 1);
        }
        if (refreshFounders) {
          await refreshFounders();
        }
      }
    } catch (error) {
      logger.error('Error handling Notion import complete:', error);
    }
  }, [setUser, refreshFounders, setCustomFieldsRefreshTrigger, closeModal]);

  return {
    startProductWalkthrough,
    handlePhoneWalkthroughClose,
    handleChromeExtensionClose,
    handleLinkedInImportComplete,
    handleNotionImportComplete,
  };
}
